import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { projects } from '../services/api';
import { Project, ProjectCreateRequest, ProjectUpdateRequest } from '../types/project';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProjectState = {
  projects: [],
  currentProject: null,
  loading: false,
  error: null,
};

export const fetchProjects = createAsyncThunk(
  'project/fetchProjects',
  async () => {
    const response = await projects.getAll();
    return response;
  }
);

export const createProject = createAsyncThunk(
  'project/createProject',
  async (projectData: ProjectCreateRequest) => {
    const response = await projects.create(projectData);
    return response;
  }
);

export const updateProject = createAsyncThunk(
  'project/updateProject',
  async (data: { 
    projectId: string; 
    projectData: ProjectUpdateRequest
  }) => {
    const response = await projects.update(data.projectId, data.projectData);
    return response;
  }
);

export const fetchProjectById = createAsyncThunk(
  'project/fetchProjectById',
  async (projectId: string) => {
    const response = await projects.getAll();
    return response.find(p => p.id === projectId) || null;
  }
);

export const deleteProject = createAsyncThunk(
  'project/deleteProject',
  async (projectId: string) => {
    await projects.delete(projectId);
    return projectId;
  }
);

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    clearCurrentProject: (state) => {
      state.currentProject = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '加载项目列表失败';
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.projects.push(action.payload);
        state.currentProject = action.payload;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.currentProject = action.payload;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        console.log('Redux: 更新项目状态', action.payload);
        const index = state.projects.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          const newProjects = [...state.projects];
          newProjects[index] = {
            ...newProjects[index],
            ...action.payload,
            updatedAt: action.payload.updatedAt
          };
          state.projects = newProjects;
        }
        if (state.currentProject?.id === action.payload.id) {
          state.currentProject = {
            ...state.currentProject,
            ...action.payload,
            updatedAt: action.payload.updatedAt
          };
        }
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.projects = state.projects.filter(p => p.id !== action.payload);
        if (state.currentProject?.id === action.payload) {
          state.currentProject = null;
        }
      });
  },
});

export const { clearCurrentProject } = projectSlice.actions;

export type { Project };
export default projectSlice.reducer; 